import { signal, TemplateRef, Service } from '@angular/core';

/**
 * Bridges per-page chrome from a routed page up into the persistent app shell. A page's `mc-page`
 * registers its templates here; the shell renders them with `ngTemplateOutlet`. This keeps the
 * toolbar, drawer, launch-pad, and app-nav mounted across navigation (only the per-page templates in
 * these slots swap) — the standalone replacement for the page-level named router outlets
 * (`page-heading`, `page-navbar-tab-links`, `page-navbar-toolbar`, `page-drawer-gutter`, `page-gutter`).
 *
 * The slots can be owned by different components at once (e.g. a feature wrapper owns the heading +
 * tab-links + toolbar + drawer while the active tab's page owns the gutter), which is why each is set
 * and cleared independently, with identity-safe clears so a departing owner can't wipe the incoming one.
 */
@Service()
export class PageChromeService {
  /** The navbar heading (rendered in the shell toolbar's heading slot). */
  readonly heading = signal<TemplateRef<unknown> | null>(null);
  /** The navbar tab links (feature tabs, e.g. Sites/URLs/Security/Themes). */
  readonly navbarTabLinks = signal<TemplateRef<unknown> | null>(null);
  /** The navbar toolbar actions (per-page/per-tab buttons). */
  readonly navbarToolbar = signal<TemplateRef<unknown> | null>(null);
  /** The drawer content (rendered in the shell's mat-sidenav — was the `page-drawer-gutter` outlet). */
  readonly drawer = signal<TemplateRef<unknown> | null>(null);
  /** The drawer nav content (rendered in the shell's mat-sidenav — was the `page-drawer-nav` outlet). */
  readonly drawerNav = signal<TemplateRef<unknown> | null>(null);
  /**
   * The "always" drawer content (rendered in the shell's mat-sidenav — was the
   * `page-drawer-gutter-always` outlet). Its presence forces the navbar drawer toggle (burger) to be
   * available regardless of breakpoint, so pages whose drawer is their only home for a control
   * (e.g. tasks' board switcher + filter) can always open it.
   */
  readonly drawerAlways = signal<TemplateRef<unknown> | null>(null);
  /** The gutter content (rendered in the shell's right-hand aside — was the `page-gutter` outlet). */
  readonly gutter = signal<TemplateRef<unknown> | null>(null);
  /**
   * Truthy (holds the owning page) when the shell should hide the entire gutter aside (app gutter +
   * page gutter) — the standalone equivalent of the old `page-gutter-hide` outlet. Used by the
   * workspace page, which shows the doc timeline in its editor instead of the gutter. Stores the
   * owner (not a bare boolean) so clears are identity-safe across create-before-destroy navigation,
   * mirroring the template slots.
   */
  readonly gutterHidden = signal<unknown>(null);

  /** Sets the heading slot (or null to clear it). */
  setHeading(template: TemplateRef<unknown> | null) {
    this.heading.set(template);
  }

  /** Sets the navbar tab-links slot (or null to clear it). */
  setNavbarTabLinks(template: TemplateRef<unknown> | null) {
    this.navbarTabLinks.set(template);
  }

  /** Sets the navbar toolbar slot (or null to clear it). */
  setNavbarToolbar(template: TemplateRef<unknown> | null) {
    this.navbarToolbar.set(template);
  }

  /** Sets the drawer slot (or null to clear it). */
  setDrawer(template: TemplateRef<unknown> | null) {
    this.drawer.set(template);
  }

  /** Sets the drawer nav slot (or null to clear it). */
  setDrawerNav(template: TemplateRef<unknown> | null) {
    this.drawerNav.set(template);
  }

  /** Sets the "always" drawer slot (or null to clear it). */
  setDrawerAlways(template: TemplateRef<unknown> | null) {
    this.drawerAlways.set(template);
  }

  /** Sets the gutter slot (or null to clear it). */
  setGutter(template: TemplateRef<unknown> | null) {
    this.gutter.set(template);
  }

  /** Marks the gutter aside hidden, owned by `owner` (see {@link gutterHidden}). */
  setGutterHidden(owner: unknown) {
    this.gutterHidden.set(owner);
  }

  /** Clears the gutter-hidden flag only if `owner` still owns it (identity-safe). See {@link clearHeading}. */
  clearGutterHidden(owner: unknown) {
    if (this.gutterHidden() === owner) {
      this.gutterHidden.set(null);
    }
  }

  /**
   * Clears the heading slot only if `template` still owns it. A page clears on destroy, but the
   * next page may have already registered its own template (navigation can create-before-destroy),
   * so the identity check prevents a departing owner from wiping the incoming one.
   */
  clearHeading(template: TemplateRef<unknown>) {
    if (this.heading() === template) {
      this.heading.set(null);
    }
  }

  /** Clears the navbar tab-links slot only if `template` still owns it. See {@link clearHeading}. */
  clearNavbarTabLinks(template: TemplateRef<unknown>) {
    if (this.navbarTabLinks() === template) {
      this.navbarTabLinks.set(null);
    }
  }

  /** Clears the navbar toolbar slot only if `template` still owns it. See {@link clearHeading}. */
  clearNavbarToolbar(template: TemplateRef<unknown>) {
    if (this.navbarToolbar() === template) {
      this.navbarToolbar.set(null);
    }
  }

  /** Clears the drawer slot only if `template` still owns it. See {@link clearHeading}. */
  clearDrawer(template: TemplateRef<unknown>) {
    if (this.drawer() === template) {
      this.drawer.set(null);
    }
  }

  /** Clears the drawer nav slot only if `template` still owns it. See {@link clearHeading}. */
  clearDrawerNav(template: TemplateRef<unknown>) {
    if (this.drawerNav() === template) {
      this.drawerNav.set(null);
    }
  }

  /** Clears the "always" drawer slot only if `template` still owns it. See {@link clearHeading}. */
  clearDrawerAlways(template: TemplateRef<unknown>) {
    if (this.drawerAlways() === template) {
      this.drawerAlways.set(null);
    }
  }

  /** Clears the gutter slot only if `template` still owns it. See {@link clearHeading}. */
  clearGutter(template: TemplateRef<unknown>) {
    if (this.gutter() === template) {
      this.gutter.set(null);
    }
  }
}
